import React, { useState, useEffect, useCallback } from "react";
import { useRecipePopup } from "../../contexts/RecipePopupContext.jsx";
import VisitorShareButton from "../visitor/VisitorShareButton.jsx";
import { HiClock, HiHeart, HiOutlineHeart } from "react-icons/hi2";
import {
  FiEdit3,
  FiTrash2,
  FiClock,
  FiUsers,
  FiUser,
  FiDownload,
} from "react-icons/fi";
import { IoRestaurantOutline } from "react-icons/io5";
import { useLanguage } from "../../contexts/LanguageContext";
import { downloadRecipePdf } from "../../utils/recipePdf.js";

/**
 * Composant de carte de recette
 * Affiche les informations d'une recette avec actions
 */
const RecipeCard = ({
  recipe,
  user,
  onFavoriteToggle,
  onEdit,
  onDelete,
  showShareForAll = false,
  showAuthor = false,
}) => {
  const { openRecipePopup } = useRecipePopup();
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const checkFavoriteStatus = useCallback(async () => {
    if (!user || !recipe) return;

    try {
      const response = await fetch(
        `http://localhost:3000/api/favorites/check/${recipe.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.isFavorite);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du favori:", error);
    }
  }, [user, recipe]);

  useEffect(() => {
    if (user && recipe) {
      checkFavoriteStatus();
    }
  }, [user, recipe, checkFavoriteStatus]);

  /**
   * Gérer l'ajout/suppression de favori
   */
  const handleToggleFavorite = async (e) => {
    e.stopPropagation(); // Empêcher la propagation du clic

    if (!user) {
      alert(t("mustBeLoggedIn"));
      return;
    }

    setLoading(true);
    try {
      const url = isFavorite
        ? `http://localhost:3000/api/favorites/remove/${recipe.id}`
        : `http://localhost:3000/api/favorites/add/${recipe.id}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        if (onFavoriteToggle) {
          onFavoriteToggle(recipe.id, !isFavorite);
        }
      } else {
        const error = await response.json();
        alert(error.message || t("errorManagingFavorites"));
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert(t("connectionError"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gérer le clic sur la carte pour afficher les détails
   */
  const handleCardClick = () => {
    openRecipePopup(recipe, user, {
      onFavoriteToggle,
      onEdit,
      onDelete,
    });
  };

  const handleDownloadPdf = async (e) => {
    e.stopPropagation();
    if (!recipe) return;
    try {
      setPdfLoading(true);
      await downloadRecipePdf(recipe, {
        labels: {
          prep: t("preparation"),
          cook: t("cooking"),
          servings: t("servings"),
          difficulty: t("difficulty") || "Difficulté",
          ingredients: t("ingredients"),
          instructions: t("instructions"),
        },
      });
    } finally {
      setPdfLoading(false);
    }
  };

  if (!recipe) return null;

  const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);

  const authorDisplay =
    recipe.author_name ||
    (recipe.first_name || recipe.last_name
      ? `${recipe.first_name || ""} ${recipe.last_name || ""}`.trim()
      : null) ||
    recipe.username ||
    null;

  return (
    <article
      className="group relative bg-white/10 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg border border-slate-100 overflow-hidden cursor-pointer hover:shadow-xl hover:border-orange-300/50 transition-all duration-300 hover:-translate-y-1 active:scale-[0.99] flex flex-col h-full ring-2 ring-transparent hover:ring-orange-400/20 touch-manipulation min-w-0"
      onClick={handleCardClick}
    >
      {/* Image container - design premium, hauteur cohérente sur mobile */}
      <div className="relative aspect-square rounded-t-xl sm:rounded-t-2xl overflow-hidden mb-2 sm:mb-3 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 shadow-inner ring-1 ring-black/10 flex-shrink-0 group/image">
        {recipe.image_url && !imageError ? (
          <>
            {/* Image avec zoom élégant */}
            <img
              src={recipe.image_url}
              alt={recipe.title || recipe.name || "Recette"}
              className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-out"
              onError={() => setImageError(true)}
              loading="lazy"
              decoding="async"
            />

            {/* Overlay gradient subtil au repos */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60 group-hover:opacity-0 transition-opacity duration-500" />

            {/* Overlay gradient au hover - effet premium */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Effet de lumière au hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

            {/* Barre de progression en bas au hover */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/80 to-transparent opacity-0 group-hover:opacity-100 transform scale-x-0 group-hover:scale-x-100 transition-all duration-500 origin-center" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-200/50 via-transparent to-transparent opacity-50" />
            <IoRestaurantOutline className="w-16 h-16 text-slate-300 relative z-10" />
          </div>
        )}

        {/* Badge difficulté - compact sur mobile */}
        <div className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 z-10">
          <span
            className={`px-1.5 py-0.5 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold backdrop-blur-md shadow-xl border ${
              recipe.difficulty === "Facile"
                ? "bg-emerald-500/95 text-white border-emerald-300/50"
                : recipe.difficulty === "Moyen"
                  ? "bg-amber-500/95 text-white border-amber-300/50"
                  : "bg-orange-500/95 text-white border-orange-300/50"
            } group-hover:scale-110 transition-transform duration-300`}
          >
            {recipe.difficulty}
          </span>
        </div>

        {/* Badge favori - plus compact sur mobile */}
        {isFavorite && (
          <div className="absolute top-1.5 left-1.5 sm:top-3 sm:left-3 z-10">
            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-red-500/95 backdrop-blur-md flex items-center justify-center shadow-xl border border-red-300/50 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <HiHeart className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white fill-white" />
            </div>
          </div>
        )}

        {/* Effet de brillance au hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none" />
      </div>

      {/* Contenu principal - padding réduit sur mobile */}
      <div className="p-2.5 sm:p-4 flex flex-col flex-1 min-w-0">
        {/* Auteur (partagé par) */}
        {showAuthor && authorDisplay && (
          <div className="flex items-center gap-1 sm:gap-1.5 mb-1.5 sm:mb-2 flex-shrink-0 text-white/80 min-w-0">
            <FiUser
              className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-orange-300/90 flex-shrink-0"
              aria-hidden
            />
            <span className="text-[10px] sm:text-xs truncate" title={authorDisplay}>
              {authorDisplay}
            </span>
          </div>
        )}

        {/* Note/rating */}
        <div className="text-amber-300 text-[10px] sm:text-xs lg:text-sm mb-0.5 sm:mb-2 flex-shrink-0 font-medium tabular-nums">
          ★ 5.0
        </div>

        {/* Titre */}
        <h3 className="font-bold text-white uppercase text-[11px] sm:text-sm mb-0.5 sm:mb-1 line-clamp-1 flex-shrink-0 min-w-0 break-words">
          {recipe.title || recipe.name || "Sans titre"}
        </h3>

        {/* Description */}
        <p className="text-white text-[10px] sm:text-xs mb-1.5 sm:mb-3 line-clamp-2 flex-shrink-0 min-h-[1.75rem] sm:min-h-[2rem]">
          {recipe.description || "Aucune description disponible"}
        </p>

        {/* Métadonnées - plus compact sur mobile */}
        <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3 flex-shrink-0 flex-wrap">
          {totalTime > 0 && (
            <div className="flex items-center gap-0.5 sm:gap-1 text-amber-200/90 text-[10px] sm:text-xs">
              <FiClock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-300 shrink-0" />
              <span className="tabular-nums">{totalTime} min</span>
            </div>
          )}
          {recipe.servings > 0 && (
            <div className="flex items-center gap-0.5 sm:gap-1 text-emerald-200/90 text-[10px] sm:text-xs">
              <FiUsers className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-300 shrink-0" />
              <span className="tabular-nums">{recipe.servings} pers.</span>
            </div>
          )}
        </div>

        {/* Actions - boutons plus petits sur mobile pour éviter débordement */}
        <div className="flex items-center justify-between mt-auto flex-shrink-0 gap-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-wrap">
            <button
              onClick={handleToggleFavorite}
              disabled={loading}
              className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full transition-all duration-200 flex items-center justify-center flex-shrink-0 touch-manipulation ${
                isFavorite
                  ? "bg-red-500/80 text-white hover:bg-red-600/80"
                  : "bg-white/20 text-white hover:bg-white/30"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              {loading ? (
                <HiClock className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : isFavorite ? (
                <HiHeart className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />
              ) : (
                <HiOutlineHeart className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
            </button>

            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(recipe);
                }}
                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-amber-500/70 text-white hover:bg-amber-500 transition-all duration-200 flex items-center justify-center flex-shrink-0 touch-manipulation"
                title="Modifier la recette"
              >
                <FiEdit3 className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
              </button>
            )}

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/15 text-white hover:bg-white/25 transition-all duration-200 flex items-center justify-center disabled:opacity-60 flex-shrink-0 touch-manipulation"
              title={t("downloadPdf") || "Télécharger la fiche PDF"}
            >
              {pdfLoading ? (
                <HiClock className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <FiDownload className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
              )}
            </button>

            {(showShareForAll || (user && recipe.user_id === user.id)) && (
              <div className="flex-shrink-0">
                <VisitorShareButton recipe={recipe} size="small" />
              </div>
            )}
          </div>

          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(recipe.id);
              }}
              className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-red-500/80 text-white hover:bg-red-600/80 transition-all duration-200 flex items-center justify-center flex-shrink-0 touch-manipulation"
              title="Supprimer la recette"
            >
              <FiTrash2 className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default RecipeCard;
